import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconPercentage } from "@tabler/icons-react";
import { useDiscountPresets } from "../../../../../context/DiscountPresetsContext";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import usePermissions from "../../../../../hooks/usePermissions";
import { DiscountPreset, DiscountType } from "../../../../../types/discounts";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { formatDiscountLabel } from "../../../../../utils/discountFormatting";
import {
  isDuplicatePresetNameError,
  validateDiscountAmount,
  validateDiscountName,
} from "../../../../../utils/discountPresetUtils";
import {
  buildNextLineDiscounts,
  DiscountableLine,
  DiscountableLineKind,
  DiscountGroup,
  findGroupForPreset,
  groupLineDiscounts,
  LineDiscountChange,
} from "../../../../../utils/lineItemDiscounts";
import DiscountValueFields from "../../../../common/DiscountValueFields";
import DiscountPresetPicker, {
  DiscountPickerValue,
} from "./DiscountPresetPicker";

export interface ApplyDiscountModalProps {
  opened: boolean;
  onClose: () => void;
  /** Lines a discount can go on (services, products, packages), in display
   *  order, each with the discounts it already carries. */
  lines: DiscountableLine[];
  /** The discount group being changed ("Change" on a summary row); omit to
   *  apply a new discount. */
  editingGroup?: DiscountGroup | null;
  /**
   * A quick discount's "+ Add": open with this preset picked, its values
   * filled in and EVERY line ticked. Ignored when `editingGroup` is set.
   */
  initialPreset?: DiscountPreset | null;
  /**
   * Persist each changed line's FULL next discount set. Reject to keep the
   * modal open with an error.
   */
  onApply: (changes: LineDiscountChange[]) => Promise<void> | void;
}

interface FormErrors {
  discount?: string;
  amount?: string;
  lines?: string;
  general?: string;
}

interface PresetPickState {
  activeGroup: DiscountGroup | null;
  discountType: DiscountType;
  amount: number;
  /** Lines that already carry the preset (to keep ticked). */
  lineKeys: string[];
}

/**
 * Form state for picking `preset`. In a fresh apply, a preset already on the
 * ticket edits that group (its values and lines); otherwise the preset's own
 * values fill the form.
 */
function presetPickState(
  preset: DiscountPreset,
  groups: DiscountGroup[],
  editingGroup: DiscountGroup | null,
): PresetPickState {
  const existingGroup = editingGroup
    ? undefined
    : findGroupForPreset(groups, preset.id);
  if (existingGroup) {
    return {
      activeGroup: existingGroup,
      discountType: existingGroup.discount_type,
      amount: existingGroup.amount,
      lineKeys: existingGroup.lineKeys,
    };
  }
  return {
    activeGroup: editingGroup,
    discountType: preset.discount_type,
    amount: preset.amount,
    lineKeys: [],
  };
}

const SECTIONS: Array<{ kind: DiscountableLineKind; title: string }> = [
  { kind: "service", title: "Services" },
  { kind: "product", title: "Products" },
  { kind: "package", title: "Packages & Specials" },
];

/**
 * Discount-first apply flow shared by the Transaction Detail modal and Quick
 * Sale: pick ONE preset (or create one inline), adjust its type/amount for this
 * ticket, tick the lines, Apply. Applying a discount never touches a line's
 * other discounts — to stack, apply another. "Change" re-opens a group with its
 * preset, values and lines; unticking a line removes the discount from it.
 *
 * The modal only computes each changed line's next full discount set (see
 * `buildNextLineDiscounts`); the host persists it via `onApply`.
 */
export default function ApplyDiscountModal({
  opened,
  onClose,
  lines,
  editingGroup = null,
  initialPreset = null,
  onApply,
}: ApplyDiscountModalProps) {
  const { isMobile } = useResponsive();
  const {
    presets,
    loading: presetsLoading,
    createPreset,
  } = useDiscountPresets();
  const canCreatePresets = usePermissions().can(
    "settingsDiscounts",
    "canManagePresets",
  );

  const [picked, setPicked] = useState<DiscountPickerValue | null>(null);
  const [discountType, setDiscountType] = useState<DiscountType>("Percentage");
  const [amount, setAmount] = useState<number | string>("");
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // The group whose entries this apply replaces: the one being changed, or the
  // ticket's existing group for a preset picked in a fresh apply.
  const [activeGroup, setActiveGroup] = useState<DiscountGroup | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groups = useMemo(() => groupLineDiscounts(lines), [lines]);

  // Read the latest presets in the open effect without re-initializing the
  // form whenever the shared catalog changes while the modal is open.
  const presetsRef = useRef(presets);
  presetsRef.current = presets;
  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setActiveGroup(editingGroup);
    if (editingGroup) {
      const preset = editingGroup.presetId
        ? presetsRef.current.find((p) => p.id === editingGroup.presetId)
        : undefined;
      setPicked(
        preset
          ? { kind: "preset", preset }
          : { kind: "existing", name: editingGroup.name },
      );
      setDiscountType(editingGroup.discount_type);
      setAmount(editingGroup.amount);
      setSelectedKeys(editingGroup.lineKeys);
    } else if (initialPreset) {
      const currentLines = linesRef.current;
      const next = presetPickState(
        initialPreset,
        groupLineDiscounts(currentLines),
        null,
      );
      setPicked({ kind: "preset", preset: initialPreset });
      setActiveGroup(next.activeGroup);
      setDiscountType(next.discountType);
      setAmount(next.amount);
      setSelectedKeys(currentLines.map((line) => line.key));
    } else {
      setPicked(null);
      setDiscountType("Percentage");
      setAmount("");
      setSelectedKeys([]);
    }
  }, [opened, editingGroup, initialPreset]);

  // An "existing" discount has no preset to apply, so it can only stay on (or
  // come off) the lines that already carry it.
  const lockedToGroup = picked?.kind === "existing";
  const isLineSelectable = (line: DiscountableLine) =>
    !lockedToGroup || (activeGroup?.lineKeys.includes(line.key) ?? false);

  const handlePick = (value: DiscountPickerValue | null) => {
    setPicked(value);
    setErrors((prev) => ({ ...prev, discount: undefined, general: undefined }));
    if (value?.kind !== "preset") return;

    const next = presetPickState(value.preset, groups, editingGroup);
    setActiveGroup(next.activeGroup);
    setDiscountType(next.discountType);
    setAmount(next.amount);
    if (next.lineKeys.length > 0) {
      setSelectedKeys((prev) =>
        Array.from(new Set([...prev, ...next.lineKeys])),
      );
    }
  };

  const toggleLine = (key: string, checked: boolean) => {
    setSelectedKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
    setErrors((prev) => ({ ...prev, lines: undefined }));
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!picked) {
      next.discount = "Choose a discount or create a new one";
    } else if (picked.kind === "create") {
      next.discount = validateDiscountName(picked.name) ?? undefined;
    }
    next.amount = validateDiscountAmount(discountType, amount) ?? undefined;
    if (!activeGroup && selectedKeys.length === 0) {
      next.lines = "Select at least one item to apply the discount to";
    }
    return next;
  };

  const handleApply = async () => {
    const validation = validate();
    if (validation.discount || validation.amount || validation.lines) {
      setErrors(validation);
      return;
    }
    if (!picked) return;

    setIsSubmitting(true);
    setErrors({});
    const value = Number(amount);
    try {
      let presetId: string | null = null;
      let name = picked.kind === "preset" ? picked.preset.name : picked.name;
      if (picked.kind === "preset") {
        presetId = picked.preset.id;
      } else if (picked.kind === "create") {
        // Create first so the new preset can be applied (and immediately shows
        // up in Settings and other open modals via the shared context).
        const created = await createPreset({
          name: picked.name.trim(),
          discount_type: discountType,
          amount: value,
        });
        setPicked({ kind: "preset", preset: created });
        presetId = created.id;
        name = created.name;
      }

      const changes = buildNextLineDiscounts(lines, {
        discount: {
          presetId,
          name,
          discount_type: discountType,
          amount: value,
        },
        selectedLineKeys: selectedKeys,
        editingGroup: activeGroup,
      });
      if (changes.length > 0) {
        await onApply(changes);
      }
      onClose();
    } catch (error) {
      console.error("Failed to apply discount:", error);
      if (picked.kind === "create" && isDuplicatePresetNameError(error)) {
        setErrors({
          discount: `A discount named "${picked.name.trim()}" already exists. Pick it from the list.`,
        });
      } else {
        setErrors({ general: "Failed to apply discount. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLine = (line: DiscountableLine) => {
    const existingLabels = line.discounts.map((d) =>
      formatDiscountLabel(d.name, d.discount_type, d.amount),
    );
    const selectable = isLineSelectable(line);
    return (
      <Checkbox
        key={line.key}
        disabled={!selectable}
        checked={selectedKeys.includes(line.key)}
        onChange={(event) => toggleLine(line.key, event.currentTarget.checked)}
        aria-label={line.name}
        label={
          <Stack gap={2}>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm">{line.name}</Text>
              <Badge size="xs" variant="light">
                {formatCurrency(line.base)}
              </Badge>
            </Group>
            {existingLabels.length > 0 && (
              <Text size="xs" c="dimmed">
                {existingLabels.join(" · ")}
              </Text>
            )}
          </Stack>
        }
      />
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingGroup ? "Change Discount" : "Apply Discount"}
      size="lg"
      fullScreen={isMobile}
      centered
    >
      <LoadingOverlay visible={isSubmitting} />
      <Stack gap="lg">
        {errors.general && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="var(--color-danger)"
          >
            {errors.general}
          </Alert>
        )}

        <DiscountPresetPicker
          presets={presets}
          value={picked}
          onChange={handlePick}
          canCreate={canCreatePresets}
          loading={presetsLoading}
          description={
            lockedToGroup
              ? "This discount isn't saved in Settings, so it can only be changed or removed on the items that already have it."
              : undefined
          }
          error={errors.discount}
        />

        <DiscountValueFields
          type={discountType}
          amount={amount}
          onTypeChange={(type) => {
            setDiscountType(type);
            setErrors((prev) => ({ ...prev, amount: undefined }));
          }}
          onAmountChange={(next) => {
            setAmount(next);
            setErrors((prev) => ({ ...prev, amount: undefined }));
          }}
          amountError={errors.amount}
        />

        <Divider />

        <Stack gap="xs">
          <Text size="sm" fw={500} c="dimmed">
            Apply to:
          </Text>
          {errors.lines && (
            <Text size="sm" c="var(--color-danger)" role="alert">
              {errors.lines}
            </Text>
          )}
        </Stack>

        {SECTIONS.map(({ kind, title }) => {
          const sectionLines = lines.filter((line) => line.kind === kind);
          if (sectionLines.length === 0) return null;
          return (
            <Paper key={kind} p="md" withBorder>
              <Text size="sm" fw={500} mb="sm">
                {title}
              </Text>
              <Stack gap="sm">{sectionLines.map(renderLine)}</Stack>
            </Paper>
          );
        })}

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            loading={isSubmitting}
            leftSection={<IconPercentage size={16} />}
          >
            Apply Discount
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
